import React, { useState, useEffect } from 'react';
import { customBaseUrl, httpGet } from '../../store/http';
import { NotificationManager } from 'react-notifications';
import { Card, Button, Row, Col, Typography, Space, Tooltip, DatePicker } from 'antd';
import axios from 'axios';
import moment from 'moment';
import {
  ArrowRightOutlined,
  WalletOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import LoanDashboardLayout from '../../components/Layout/LoanDashboardLayout';
import { hideLoader, showLoader } from '../../utils/loader';
import AdminLayout from "../../components/Layout";
import { useNavigate } from 'react-router-dom';
import slugs from '../../resources/slugs';
import { useMatStyles } from './style';

const { Title, Text } = Typography;

const LoanAdminDashboard = ({ from }) => {
  const navigate = useNavigate();
  const [loanAnalytics, setLoanAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportDates, setReportDates] = useState([moment().subtract(30, 'days'), moment()]);
  const classes = useMatStyles();

  useEffect(() => {
    const fetchLoanAnalytics = async () => {
      showLoader(); // Show loader
      try {
        const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
        if (!token) {
          window.location.href = '/loan-admin-login';
          return;
        }
        const res = await httpGet(
          `${customBaseUrl.loanUrl}/api/v1/admin/analytics/loans`,
          null,
          false,
          false,
          token
        );
        if (res.status) {
          setLoanAnalytics(res.data);
        } else {
          NotificationManager.error(res.message || 'Failed to fetch loan analytics');
        }
      } catch (error) {
        console.error('Error fetching loan analytics:', error);
        NotificationManager.error('Error fetching loan analytics');
      } finally {
        hideLoader(); // Hide loader
        setLoading(false);
      }
    };

    fetchLoanAnalytics();
  }, []);

  const handleDownloadReport = async () => {
    if (!reportDates || reportDates.length !== 2) {
      NotificationManager.warning('Please select a date range');
      return;
    }
    showLoader();
    try {
      const [start, end] = reportDates;
      const startDate = start.format('YYYY-MM-DD');
      const endDate = end.format('YYYY-MM-DD');
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');

      const response = await axios.get(`${customBaseUrl.loanUrl}/api/v1/admin/reports/loans`, {
        params: { startDate, endDate },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `LoanReport_${startDate}_to_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      NotificationManager.success('Report downloaded successfully');
    } catch (err) {
      console.error('Download error:', err);
      NotificationManager.error('Failed to download report');
    } finally {
      hideLoader();
    }
  };
  const Layout = from === "admin" ? AdminLayout : LoanDashboardLayout;

  if (loading) {
    return (
      <Layout>
        <div>Loading analytics...</div>
      </Layout>
    );
  }
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const MetricCard = ({ title, value, icon, color }) => (
    <div className={classes.dashItem}>
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
        <Text className={classes.diTop}>{title}</Text>
        <div style={{ padding: 8, background: `${color}15`, borderRadius: 10, display: 'flex' }}>
          {React.cloneElement(icon, { style: { color, fontSize: 18 } })}
        </div>
      </div>
      <Title level={3} className={classes.dibot} style={{ margin: 0 }}>{value}</Title>
    </div>
  );

  return (
    <Layout>
      <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ margin: 0, fontWeight: 800 }}>Overview</Title>
          <Text type="secondary">Welcome to your loan administration dashboard</Text>
        </div>
        <Button
          type="primary"
          size="large"
          icon={<ArrowRightOutlined />}
          style={{
            height: 48,
            borderRadius: 12,
            background: '#FF4B01',
            border: 'none',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center'
          }}
          onClick={() => navigate(from !== 'admin' ? slugs.apply_for_loan : slugs.apply_for_loan_admin, { state: { from } })}
        >
          Apply for Loan
        </Button>
      </div>

      <Card
        style={{ marginBottom: 24, borderRadius: 16, border: '1px solid #f0f0f0' }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <Row align="middle" justify="space-between">
          <Col>
            <Space size={16}>
              <Text strong>Export Loan Data:</Text>
              <DatePicker.RangePicker
                value={reportDates}
                onChange={(dates) => setReportDates(dates)}
                style={{ borderRadius: 8 }}
              />
            </Space>
          </Col>
          <Col>
            <Button
              onClick={handleDownloadReport}
              style={{
                borderRadius: 8,
                fontWeight: 600,
                color: '#FF4B01',
                borderColor: '#FF4B01'
              }}
            >
              Download Excel Report
            </Button>
          </Col>
        </Row>
      </Card>

      {loanAnalytics && (
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          {/* Main Stats Row */}
          <div style={{ display: 'flex', gap: 24 }}>
            <MetricCard
              title="Total Applications"
              value={loanAnalytics.totalApplications}
              icon={<FileTextOutlined />}
              color="#FF4B01"
            />
            <MetricCard
              title="Total Applied"
              value={formatCurrency(loanAnalytics.totalAmountApplied)}
              icon={<WalletOutlined />}
              color="#0086E8"
            />
            <MetricCard
              title="Total Disbursed"
              value={formatCurrency(loanAnalytics.totalAmountDisbursed)}
              icon={<CheckCircleOutlined />}
              color="#27AE60"
            />
          </div>

          <Row gutter={[24, 24]}>
            <Col xs={24} lg={16}>
              <Card
                title={<Space><ClockCircleOutlined /> Applications By Status</Space>}
                style={{ borderRadius: 16, border: '1px solid #f0f0f0' }}
              >
                <Row gutter={[16, 16]}>
                  {Object.entries(loanAnalytics.applicationsByStatus).map(([status, count]) => (
                    <Col xs={12} sm={8} key={status}>
                      <div
                        onClick={() => navigate(slugs.loans_by_status.replace(':status', status), { state: { from } })}
                        style={{
                          padding: '16px',
                          background: '#f8f9fa',
                          borderRadius: 12,
                          cursor: 'pointer',
                          border: '1px solid #eee',
                          transition: 'all 0.2s'
                        }}
                        className="status-hover"
                      >
                        <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', fontWeight: 600 }}>{status}</Text>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', marginTop: 4 }}>{count}</div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card
                title={<Space><GlobalOutlined /> Quick Stats</Space>}
                style={{ borderRadius: 16, border: '1px solid #f0f0f0', height: '100%' }}
              >
                <Space direction="vertical" size={20} style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary" block>Average Loan Amount</Text>
                    <Text style={{ fontSize: 18, fontWeight: 700 }}>{formatCurrency(loanAnalytics.averageLoanAmount)}</Text>
                  </div>
                  <div style={{ height: 1, background: '#f0f0f0' }} />
                  <div>
                    <Text type="secondary" block style={{ marginBottom: 8 }}>Applications By Type</Text>
                    {Object.entries(loanAnalytics.applicationsByType).map(([type, count]) => (
                      <div key={type} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Text>{type}</Text>
                        <Text weight={700}>{count}</Text>
                      </div>
                    ))}
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </Space>
      )}

      {!loanAnalytics && !loading && (
        <Card style={{ textAlign: 'center', padding: '40px 0', borderRadius: 16 }}>
          <Text type="secondary">No analytics data available at the moment.</Text>
        </Card>
      )}

      <style>{`
        .status-hover:hover {
          border-color: #FF4B01 !important;
          background: #fff !important;
          box-shadow: 0 4px 12px rgba(255, 75, 1, 0.08);
          transform: translateY(-2px);
        }
      `}</style>
    </Layout>
  );
};

export default LoanAdminDashboard;